import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  USER_ROLE_ADMIN,
} from 'common/userRoles';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CATEGORY_FEATURE,
  PROPERTY_ATTRIBUTE_LEVEL_APARTMENT,
} from 'common/propertyAttributeConstants';

import {
  LISTING_FORM_TYPE_APARTMENT_PROPERTY_ATTRIBUTES,
} from 'common/listingFormFields';

import {
  SR_API_ACTION_ADMIN_UPDATE_ITEM,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_APARTMENTS,
  DATA_TABLE_VIEW_ID_APARTMENT_BY_ID,
} from 'common/dataTableConstants';

import {
  SCREEN_ID_ADMIN_APARTMENT,
} from 'client/constants/screenIds';

import {
  dataTable,
  actionDataTableLoadItems,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import {
  isSelfShowingListing,
} from 'common/listingHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrPage from 'client/components/SrPage';
import SrLink from 'client/components/SrLink/SrLink';
import SrTable from 'client/components/SrTable';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrApartmentForm from 'client/components/SrApartmentForm';
import SrApartmentBuildingModal from 'client/components/SrApartmentBuildingModal';
import SrFloorPlanModal from 'client/components/SrFloorPlanModal';
import SrApartmentSpecialDealModal from 'client/components/SrApartmentSpecialDealModal';
import SrTableApartmentBuildings from 'client/components/SrTableApartmentBuildings';
import SrTableFloorPlans from 'client/components/SrTableFloorPlans';
import SrPropertyAttributeListingFieldFormModal from 'client/components/SrPropertyAttributeListingFieldFormModal';
import SrPropertyAttributeFeatureModal from 'client/components/SrPropertyAttributeFeatureModal';
import SrTablePropertyAttributes from 'client/components/SrTablePropertyAttributes';
import SrTablePropertyAttributeFeature from 'client/components/SrTablePropertyAttributeFeature';
import SrTableApartmentSpecialDeals from 'client/components/SrTableApartmentSpecialDeals';
import SrTableApartmentListingsWithoutFloorPlan from 'client/components/SrTableApartmentListingsWithoutFloorPlan';
import SrDataTableMedia from 'client/components/SrDataTableMedia';
import SrAdminMediaListTable from 'client/components/SrAdminMediaListTable';
import SrAdminPhotosEditor, {
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT,
  SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT_COMMUNITY_PHOTOS,
} from 'client/components/SrAdminPhotosEditor';
import SrPartnerCsvListingsUploader from 'client/components/SrPartnerCsvListingsUploader';
import SrPartnerCsvListingsUndo from 'client/components/SrPartnerCsvListingsUndo';
import SrAdminApartmentSelfTourMultiFamilyModal from 'client/components/SrAdminApartmentSelfTourMultiFamilyModal';

import postWithState from 'client/actions/postWithState';
import allowedRoles from 'client/helpers/allowedRoles';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

function getDataTableSettings({ apartmentId }) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_APARTMENTS,
    viewId: [
      DATA_TABLE_VIEW_ID_APARTMENT_BY_ID,
      apartmentId,
    ].join(':'),
    count: 1,
    filter: {
      'apartments.id': parseInt(apartmentId, 10),
    },
  };
  return dataTableSettings;
}

const SrSectionText = ({ children, isLink, onClick }) => (
  <SrText
    size='xl'
    font='whitney-sc'
    onClick={onClick}
    {...(onClick || isLink ? { cursor:'pointer', color: 'blue300' } : {})}
  >
    {children}
  </SrText>
);

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_ADMIN_APARTMENT)
@withScrollToTop
@dataTable(({ match }) => {
  return getDataTableSettings({
    apartmentId: match.params.apartmentId,
  });
})
class AdminApartmentPage extends Component {
  static propTypes = {
    items: PropTypes.array.isRequired,
    match: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
  }

  static fetchData(match, inject) {
    const dataTableSettings = getDataTableSettings({
      apartmentId: match.params.apartmentId,
    });

    actionInitializeDataTableState(inject, dataTableSettings);
    return actionDataTableLoadItems(inject, dataTableSettings);
  }

  constructor(props) {
    super(props);

    this.state = {
      isApartmentModalOpen: false,
      isApartmentSpecialModalOpen: false,
      isApartmentBuildingModalOpen: false,
      isFloorPlanModalOpen: false,
      isPropertyAttributeFeatureModalOpen: false,
      isPropertyAttributeListModalOpen: false,
      isCoverPhotosEditorModalOpen: false,
      isCommunityPhotosEditorModalOpen: false,
      isSelfTourModalOpen: false,
      isMediaModalOpen: false,
    };
  }

  _reloadApartment = () => {
    const { match, dispatch } = this.props;
    const dataTableSettings = getDataTableSettings({
      apartmentId: match.params.apartmentId,
    });
    return dispatch(actionDataTableLoadItems, dataTableSettings);
  }

  _onUpdateApartmentForm = () => {
    this._reloadApartment();
    this._onToggleApartmentModal();
  }

  _onApartmentSpecialDealChange = () => {
    this._reloadApartment();
    this._onToggleApartmentSpecialDealModal();
  }

  _onApartmentBuildingChange = () => {
    this._reloadApartment();
    this._onToggleApartmentBuildingModal();
  }

  _onFloorPlanChange = () => {
    this._reloadApartment();
    this._onToggleFloorPlanModal();
  }

  _onPropertyAttributeListChange = () => {
    this._reloadApartment();
    this._onTogglePropertyAttributeListModal();
  }

  _onPropertyAttributeFeatureChange = () => {
    this._reloadApartment();
    this._onTogglePropertyAttributeFeatureModal();
  }

  _onToggleApartmentModal = () => {
    this.setState({
      isApartmentModalOpen: !this.state.isApartmentModalOpen,
    });
  }

  _onToggleApartmentSpecialDealModal = () => {
    this.setState({
      isApartmentSpecialModalOpen: !this.state.isApartmentSpecialModalOpen,
    });
  }

  _onToggleApartmentBuildingModal = () => {
    this.setState({
      isApartmentBuildingModalOpen: !this.state.isApartmentBuildingModalOpen,
    });
  }

  _onToggleFloorPlanModal = () => {
    this.setState({
      isFloorPlanModalOpen: !this.state.isFloorPlanModalOpen,
    });
  }

  _onTogglePropertyAttributeListModal = () => {
    this.setState({
      isPropertyAttributeListModalOpen: !this.state.isPropertyAttributeListModalOpen,
    });
  }

  _onTogglePropertyAttributeFeatureModal = () => {
    this.setState({
      isPropertyAttributeFeatureModalOpen: !this.state.isPropertyAttributeFeatureModalOpen,
    });
  }

  _onToggleCoverPhotosEditorModal = () => {
    this.setState({
      isCoverPhotosEditorModalOpen: !this.state.isCoverPhotosEditorModalOpen,
    });
  }

  _onApartmentCoverPhotosEdit = (photoUrls) => {
    const { items, dispatch } = this.props;
    if (items.length === 0) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'apartments',
        where: {
          id: items[0].id,
        },
        updates: {
          photoUrls,
        },
      },
      onSuccess: () => {
        this._reloadApartment();
        this._onToggleCoverPhotosEditorModal();
      },
    });
  }

  _onToggleCommunityPhotosEditorModal = () => {
    this.setState({
      isCommunityPhotosEditorModalOpen: !this.state.isCommunityPhotosEditorModalOpen,
    });
  }

  _onApartmentCommunityPhotosEdit = (communityPhotoUrls) => {
    const { items, dispatch } = this.props;
    if (items.length === 0) {
      return;
    }

    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_UPDATE_ITEM,
      payload: {
        table: 'apartments',
        where: {
          id: items[0].id,
        },
        updates: {
          communityPhotoUrls,
        },
      },
      onSuccess: () => {
        this._reloadApartment();
        this._onToggleCommunityPhotosEditorModal();
      },
    });
  }

  _onToggleSelfTourModal = () => {
    this.setState({
      isSelfTourModalOpen: !this.state.isSelfTourModalOpen,
    });
  }

  _onOpenMediaClick = () => {
    this.setState({
      isMediaModalOpen: true,
    });
  }

  _onCloseMediaModal = () => {
    this.setState({
      isMediaModalOpen: false,
    });
  }

  render() {
    const {
      isApartmentModalOpen,
      isApartmentSpecialModalOpen,
      isApartmentBuildingModalOpen,
      isFloorPlanModalOpen,
      isPropertyAttributeFeatureModalOpen,
      isPropertyAttributeListModalOpen,
      isCoverPhotosEditorModalOpen,
      isCommunityPhotosEditorModalOpen,
      isSelfTourModalOpen,
      isMediaModalOpen,
    } = this.state;

    const {
      items,
      dispatch,
    } = this.props;

    if (!items.length) {
      return (
        <div className='loading-ellipses' />
      );
    }

    const {
      apartmentBuildings,
      apartmentSpecialDeals,
      propertyAttributes,
      floorPlans,
      listings,
      photoUrls,
      communityPhotoUrls,
      ...apartment
    } = items[0];

    return (
      <SrPage navbarProps={{ redirectLogoTo: '/admin/apartments' }}>
        <SrBox className='width-90'>
          <SrBox marginBottom={24}>
            <SrBox>
              <SrFlex justify='between' marginBottom={10}>
                <SrFlex>
                  <SrSectionText>apartment</SrSectionText>
                  <SrBox width={20} />
                  <SrLink target='_blank' to={`/apartment/${apartment.id}`}>
                    <SrSectionText isLink>
                      renter page
                    </SrSectionText>
                  </SrLink>
                </SrFlex>
              </SrFlex>
              <SrTable
                headers={[
                  { content: 'name' },
                  { content: 'units' },
                  { content: 'unit setup instructions' },
                  { content: 'cover photos' },
                  { content: 'community photos' },
                  { content: 'features' },
                  { content: 'attributes' },
                  { content: 'self tour flow' },
                  { content: 'media' },
                  { content: 'options' },
                ]}
                rows={[{
                  cells: [
                    { content: apartment.name },
                    { content: apartment.numberOfUnits },
                    { content: apartment.jobSetupInstructions || '--' },
                    {
                      content: (
                        <SrText
                          onClick={this._onToggleCoverPhotosEditorModal}
                          color='blue300'
                          cursor='pointer'
                        >
                          Edit photos
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          onClick={this._onToggleCommunityPhotosEditorModal}
                          color='blue300'
                          cursor='pointer'
                        >
                          Edit photos
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          cursor='pointer'
                          color='blue300'
                          data-apartment-id={apartment.id}
                          onClick={this._onTogglePropertyAttributeFeatureModal}
                        >
                          Edit Features
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          cursor='pointer'
                          color='blue300'
                          data-id={apartment.id}
                          onClick={this._onTogglePropertyAttributeListModal}
                        >
                          Edit Attributes
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          cursor='pointer'
                          color='blue300'
                          data-id={apartment.id}
                          onClick={this._onToggleSelfTourModal}
                        >
                          Edit Self Tour Flow
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          cursor='pointer'
                          color='blue300'
                          onClick={this._onOpenMediaClick}
                        >
                          Edit Media
                        </SrText>
                      ),
                    },
                    {
                      content: (
                        <SrText
                          cursor='pointer'
                          color='blue300'
                          data-id={apartment.id}
                          onClick={this._onToggleApartmentModal}
                        >
                          Edit
                        </SrText>
                      ),
                    },
                  ],
                }]}
              />
            </SrBox>
          </SrBox>
          <SrBox marginBottom={24}>
            <SrFlex justify='between' marginBottom={10}>
              <SrSectionText>special deals</SrSectionText>
              <SrSectionText onClick={this._onToggleApartmentSpecialDealModal}>
                add special deal
              </SrSectionText>
            </SrFlex>
            <SrTableApartmentSpecialDeals
              apartmentSpecialDeals={apartmentSpecialDeals}
              floorPlans={floorPlans}
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          <SrBox marginBottom={24}>
            <SrFlex justify='between' marginBottom={10}>
              <SrSectionText>apartment buildings</SrSectionText>
              <SrSectionText onClick={this._onToggleApartmentBuildingModal}>
                add building
              </SrSectionText>
            </SrFlex>
            <SrTableApartmentBuildings
              apartmentId={apartment.id}
              apartmentBuildings={apartmentBuildings}
              propertyAttributes={propertyAttributes}
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          <SrBox marginBottom={24}>
            <SrFlex justify='between' marginBottom={10}>
              <SrSectionText>floor plans</SrSectionText>
              <SrSectionText onClick={this._onToggleFloorPlanModal}>
                add floor plan
              </SrSectionText>
            </SrFlex>
            <SrTableFloorPlans
              apartmentId={apartment.id}
              floorPlans={floorPlans}
              listings={listings}
              propertyAttributes={propertyAttributes}
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          <SrBox marginBottom={24}>
            <SrTableApartmentListingsWithoutFloorPlan
              listings={listings.filter((listing) => !listing.floorPlanId)}
              floorPlans={floorPlans}
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          <SrBox marginBottom={24}>
            <SrFlex justify='between' marginBottom={10}>
              <SrSectionText>property features</SrSectionText>
            </SrFlex>
            <SrTablePropertyAttributeFeature
              propertyAttributes={propertyAttributes
                .filter(({ category }) => category === PROPERTY_ATTRIBUTE_CATEGORY_FEATURE)
              }
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          <SrBox marginBottom={24}>
            <SrFlex justify='between' marginBottom={10}>
              <SrSectionText>active attributes</SrSectionText>
            </SrFlex>
            <SrTablePropertyAttributes
              propertyAttributes={propertyAttributes
                .filter(({ category }) => category === PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD)
              }
              onChange={this._reloadApartment}
              dispatch={dispatch}
            />
          </SrBox>
          {isCoverPhotosEditorModalOpen && (
            <SrDarkModal onClose={this._onToggleCoverPhotosEditorModal}>
              <SrModalContent style={{ width: 1040, height: '94vh', marginTop: 4 }}>
                <SrAdminPhotosEditor
                  filePath={SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT}
                  photoUrls={photoUrls}
                  onSave={this._onApartmentCoverPhotosEdit}
                />
              </SrModalContent>
            </SrDarkModal>
          )}
          {isCommunityPhotosEditorModalOpen && (
            <SrDarkModal onClose={this._onToggleCommunityPhotosEditorModal}>
              <SrModalContent style={{ width: 1040, height: '94vh', marginTop: 4 }}>
                <SrAdminPhotosEditor
                  filePath={SR_ADMIN_PHOTOS_EDITOR_FILE_PATH_APARTMENT_COMMUNITY_PHOTOS}
                  photoUrls={communityPhotoUrls}
                  onSave={this._onApartmentCommunityPhotosEdit}
                />
              </SrModalContent>
            </SrDarkModal>
          )}
          {isApartmentModalOpen && (
            <SrDarkModal onClose={this._onToggleApartmentModal}>
              <SrModalContent style={{ maxWidth: 440, maxHeight: 542 }}>
                <SrFlex justify='center'>
                  <SrText size='2xl' align='center'>
                    Apartment
                  </SrText>
                </SrFlex>
                <SrBox marginTop={20}>
                  <SrApartmentForm
                    apartment={apartment}
                    dispatch={dispatch}
                    onChange={this._onUpdateApartmentForm}
                  />
                </SrBox>
              </SrModalContent>
            </SrDarkModal>
          )}
          {isApartmentSpecialModalOpen && (
            <SrApartmentSpecialDealModal
              apartmentSpecialDeal={{
                apartmentId: apartment.id,
              }}
              floorPlans={floorPlans}
              onChange={this._onApartmentSpecialDealChange}
              onClose={this._onToggleApartmentSpecialDealModal}
              dispatch={dispatch}
            />
          )}
          {isApartmentBuildingModalOpen && (
            <SrApartmentBuildingModal
              apartmentBuilding={{
                apartmentId: apartment.id,
              }}
              onChange={this._onApartmentBuildingChange}
              onClose={this._onToggleApartmentBuildingModal}
              dispatch={dispatch}
            />
          )}
          {isFloorPlanModalOpen && (
            <SrFloorPlanModal
              floorPlan={{
                apartmentId: apartment.id,
              }}
              propertyAttributes={[]}
              onChange={this._onFloorPlanChange}
              onClose={this._onToggleFloorPlanModal}
              dispatch={dispatch}
            />
          )}
          {isPropertyAttributeFeatureModalOpen && (
            <SrPropertyAttributeFeatureModal
              propertyAttributes={propertyAttributes
                .filter(({ category }) => category === PROPERTY_ATTRIBUTE_CATEGORY_FEATURE)
              }
              apartmentId={apartment.id}
              propertyAttributeLevel={PROPERTY_ATTRIBUTE_LEVEL_APARTMENT}
              onClose={this._onTogglePropertyAttributeFeatureModal}
              onChange={this._onPropertyAttributeFeatureChange}
              dispatch={dispatch}
            />
          )}
          {isPropertyAttributeListModalOpen && (
            <SrPropertyAttributeListingFieldFormModal
              title={`Apartment "${apartment.name}"`}
              formType={LISTING_FORM_TYPE_APARTMENT_PROPERTY_ATTRIBUTES}
              propertyAttributes={propertyAttributes}
              apartmentId={apartment.id}
              onChange={this._onPropertyAttributeListChange}
              onClose={this._onTogglePropertyAttributeListModal}
              dispatch={dispatch}
            />
          )}
          <SrPartnerCsvListingsUploader
            apartmentId={apartment.id}
          />
          <SrPartnerCsvListingsUndo />
          {isSelfTourModalOpen && (
            <SrAdminApartmentSelfTourMultiFamilyModal
              apartment={apartment}
              apartmentBuildings={apartmentBuildings}
              listings={listings.filter((listing) => isSelfShowingListing(listing))}
              onChange={this._reloadApartment}
              onClose={this._onToggleSelfTourModal}
              dispatch={dispatch}
            />
          )}

          {isMediaModalOpen && (
            <SrDarkModal onClose={this._onCloseMediaModal}>
              <SrModalContent>
                <SrText size='28' weight='600' paddingBottom='10x' display='block'>
                  {`Media for ${apartment.name}`}
                </SrText>
                <SrDataTableMedia apartmentId={apartment.id}>
                  {({ mediaList, reloadMediaList, reloadMediaById }) => (
                    <SrAdminMediaListTable
                      mediaList={mediaList}
                      reloadMediaList={reloadMediaList}
                      reloadMediaById={reloadMediaById}
                      dispatch={dispatch}
                      createMediaDefaultValues={{
                        apartmentId: apartment.id,
                      }}
                      createMediaTitle={`Media for ${apartment.name}`}
                    />
                  )}
                </SrDataTableMedia>
              </SrModalContent>
            </SrDarkModal>
          )}
        </SrBox>
      </SrPage>
    );
  }
}

AdminApartmentPage.displayName = 'AdminApartmentPage';

export default AdminApartmentPage;
